import React from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {frase} from '../../frase';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faAngleDown, faAngleUp} from '@fortawesome/free-solid-svg-icons';
import {skinCorrente, eColore, inAnteprima} from '../../skin';
import {pieDiPagina} from '../../moduli/leggi';
import {useSkin} from '../../moduli/useSkin';
import './styles/Footer.css';
import './styles/Global.css';
import {indirizzoImmagine} from '../../utils/immagini';

/**
 * IL PIEDE DI PAGINA.
 *
 * E' UN MODULO DELLA SKIN (moduli/catalogo.json, «pieDiPagina»): il
 * backoffice decide, per skin,
 *   - CONTENUTO: quali colonne ci sono - Informazioni (coi tre testi che
 *     diventano le pagine Regolamento, Assistenza, Gioco responsabile),
 *     Gioco responsabile (18+ e i loghi degli enti), Sistemi di pagamento
 *     (spenta finche' non c'e' un fornitore), la fascia legale col testo;
 *   - FORMA: tre colonne o una riga; aperto in Home e chiuso altrove,
 *     sempre aperto, sempre chiuso;
 *   - COLORI: eredita dalla skin, o sfondo e testo propri.
 * Il sito legge da moduli/leggi.js; dove la skin non ha scritto niente vale
 * il «di serie» del catalogo, che e' il piede com'era prima.
 *
 * Prima c'erano cinque colonne in inglese - Sportsbook, Live Chat 24/7,
 * First Deposit Bonus - che portavano a indirizzi inesistenti, e quattro
 * carte di credito scelte a mano. Erano del white label, non nostre.
 *
 * I quattro loghi di pagamento qui sotto sono un ESEMPIO della forma,
 * presi da /assets, e stanno sotto l'etichetta «da collegare»: il
 * versamento da soli non c'e' ancora.
 */
const INFORMAZIONI = [
  {chiave: 'regolamento', etichetta: frase('Regolamento'), percorso: '/regolamento'},
  {chiave: 'assistenza', etichetta: frase('Assistenza'), percorso: '/assistenza'},
  {chiave: 'giocoResponsabile', etichetta: frase('Gioco responsabile'), percorso: '/gioco-responsabile'},
];

const PAGAMENTI_ESEMPIO = ['Visa', 'MasterCard', 'PayPal', 'Skrill', 'Neteler'];

export default function Footer({host}) {
  const {t} = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [aperto, setAperto] = React.useState(false);
  useSkin();
  const skin = skinCorrente();
  const piede = pieDiPagina();
  const imgUrl = typeof host === 'undefined' ? '' : host;

  /* La forma «apertura»: di serie sta aperto sulla Home e chiuso altrove,
     con la freccia per aprirlo. */
  const inHome = (location && location.pathname) === '/';
  const sempreAperto = piede.apertura === 'sempreAperto' || (piede.apertura !== 'sempreChiuso' && inHome);
  const mostra = sempreAperto || aperto;

  /* I colori propri del modulo, se la skin li ha scelti: si scrivono come
     variabili sul piede e basta, il resto del sito non li vede. */
  const stile = {};
  if (piede.colori) {
    if (eColore(piede.colori.sfondo)) stile['--lvc-pie-sfondo'] = piede.colori.sfondo.trim();
    if (eColore(piede.colori.testo)) stile['--lvc-pie-testo'] = piede.colori.testo.trim();
  }

  const testoDi = (colonna, chiave) => (typeof colonna[chiave] === 'string' ? colonna[chiave].trim() : '');
  const loghiEnti = Array.isArray(piede.giocoResponsabile.loghiEnti)
    ? piede.giocoResponsabile.loghiEnti.filter((u) => typeof u === 'string' && u.trim()) : [];
  const testoResponsabile = testoDi(piede.giocoResponsabile, 'testo');
  const testoLegale = testoDi(piede.legale, 'testo');
  const colonneAccese = ['informazioni', 'giocoResponsabile', 'pagamenti'].filter((c) => piede[c].acceso && (c !== 'pagamenti' || inAnteprima()));

  return (
    <footer style={stile}
      className={'footer-container border' + (sempreAperto ? ' vetrina-piede' : '')
        + (piede.colonne === 'una' ? ' piede-una-riga' : '')}>
      {!sempreAperto && (
        <div className="borderReverse">
          <div className="toggleMenu border">
            <button type="button" className="footer-apri"
              aria-expanded={aperto}
              aria-label={aperto ? t('Chiudi il piede di pagina') : t('Apri il piede di pagina')}
              onClick={() => setAperto((a) => !a)}>
              <FontAwesomeIcon icon={aperto ? faAngleUp : faAngleDown} size={'lg'}
                className={'light-grey footer-arrow'}/>
            </button>
          </div>
        </div>
      )}

      <div className={'footer-box ' + (mostra ? 'show' : '')}>
        {colonneAccese.length > 0 && (
          <div className="footer-content piede-griglia">

            {piede.informazioni.acceso && (
              <nav className="piede-colonna" aria-label={t('Informazioni')}>
                <p className="piede-titolo">{t('Informazioni')}</p>
                {INFORMAZIONI.map((v) => (
                  <button type="button" key={v.percorso} className="piede-voce"
                    onClick={() => navigate(v.percorso)}>{t(v.etichetta)}</button>
                ))}
                {/* Le note «da scrivere», «da caricare», «da collegare» sono per chi compone la skin: si vedono solo in anteprima. */}
                {inAnteprima() && INFORMAZIONI.some((v) => !testoDi(piede.informazioni, v.chiave)) && (
                  <p className="piede-nota">
                    <span className="piede-etichetta">{t('da scrivere')}</span>
                    {/* Una frase intera con dentro l'elenco e il numero: in
                        un'altra lingua l'ordine delle parole puo' cambiare,
                        quindi non si incolla pezzo per pezzo. */}
                    {t('I testi delle pagine li scrive la skin dal backoffice (Moduli → Piè di pagina): {{pagine}} non ancora scritti.', {
                      count: INFORMAZIONI.filter((v) => !testoDi(piede.informazioni, v.chiave)).length,
                      pagine: INFORMAZIONI.filter((v) => !testoDi(piede.informazioni, v.chiave)).map((v) => t(v.etichetta)).join(', '),
                    })}
                  </p>
                )}
              </nav>
            )}

            {piede.giocoResponsabile.acceso && (
              <div className="piede-colonna">
                <p className="piede-titolo">{t('Gioco responsabile')}</p>
                <div className="piede-loghi">
                  <img src={imgUrl + '/assets/18.png'} alt={t('Vietato ai minori di 18 anni')} className="piede-18"/>
                  {loghiEnti.length > 0
                    ? loghiEnti.map((u) => <img key={u} src={indirizzoImmagine(u)} alt="" className="piede-ente"/>)
                    : inAnteprima() && <><span className="piede-logo-vuoto">{t('[LOGO ENTE]')}</span><span className="piede-logo-vuoto">{t('[LOGO ENTE]')}</span></>}
                </div>
                <p className="piede-testo">
                  {testoResponsabile || t('Il gioco è vietato ai minori di 18 anni e può causare dipendenza.')}
                </p>
                {loghiEnti.length === 0 && inAnteprima() && (
                  <p className="piede-nota">
                    <span className="piede-etichetta">{t('da caricare')}</span>
                    {t('I loghi degli enti si caricano dal backoffice (Moduli → Piè di pagina).')}
                  </p>
                )}
              </div>
            )}

            {piede.pagamenti.acceso && inAnteprima() && (
              <div className="piede-colonna larga">
                <p className="piede-titolo">{t('Sistemi di pagamento')}</p>
                <div className="piede-loghi">
                  {PAGAMENTI_ESEMPIO.map((n) => (
                    <img key={n} src={imgUrl + '/assets/' + n + '.png'} alt={n} className="piede-pagamento"/>
                  ))}
                </div>
                <p className="piede-nota">
                  <span className="piede-etichetta">{t('da collegare')}</span>
                  {t('Qui vanno SOLO i metodi accesi sulla skin; senza metodi accesi il blocco non compare. Il versamento da soli non c’è ancora: questi loghi sono un esempio della forma.')}
                </p>
              </div>
            )}
          </div>
        )}

        {piede.legale.acceso && (
          <div className="licence-container">
            <div className="footer-content piede-legale">
              {skin && skin.logoUrl
                ? <img src={indirizzoImmagine(skin.logoUrl)} alt={skin.name || ''} className="piede-marchio"/>
                : <span className="piede-logo-vuoto">{skin && skin.name ? skin.name : (inAnteprima() ? t('[LOGO SKIN]') : '')}</span>}
              <p>{testoLegale || (inAnteprima()
                ? t('[TESTO LEGALE E LICENZA DELLA SKIN — si scrive dal backoffice: Moduli → Piè di pagina → Fascia legale]')
                : t('Il gioco è vietato ai minori di 18 anni e può causare dipendenza.'))}</p>
            </div>
          </div>
        )}
      </div>
    </footer>
  );
}
